/* @flow */
import { OrderedMap } from 'immutable';
import { formatCurrency } from './currency';

import { getIncomeTypeIndex } from './incomeCalculations';
import { getIncomeSummaryString } from '../components/IncomeCalculator/IncomeSummary/IncomeSummary';
import { getExpenseSummaryString } from '../components/IncomeCalculator/ExpenseRow/ExpenseRow';

import type { Expense, Income } from '../components/IncomeCalculator/IncomeCalculator';
import type { PDFDocument } from 'pdfkit/js/pdfkit.standalone';
import type { TFunction } from 'react-i18next';

export function formatIncomeRows(t: TFunction, incomes: OrderedMap<string, Income>) {
  if (incomes.isEmpty()) return null;
  const incomeArray = [];
  incomes.forEach((income) => {
    const incomeTypeIndex = getIncomeTypeIndex(income, incomes) + 1;
    const { calculatedTotal } = income;
    const incomeString = getIncomeSummaryString({ ...income, incomeTypeIndex, t });
    incomeArray.push({ text: incomeString, amount: formatCurrency(calculatedTotal) });
  });

  return incomeArray;
}

export function formatExpenseRows(t: TFunction, expenses: OrderedMap<string, Expense>) {
  if (expenses.isEmpty()) return null;
  const expensesArray = [];
  expenses.forEach((expense) => {
    const { calculatedTotal } = expense;
    const expenseString = getExpenseSummaryString({ ...expense, t });
    expensesArray.push({ text: expenseString, amount: formatCurrency(calculatedTotal) });
  });

  return expensesArray;
}

function renderDocument(doc: PDFDocument, array?: Array<any>) {
  if (array) {
    let x = doc.x;
    let y = doc.y;

    array.forEach((value) => {
      const docText = value.text;
      const result = docText.indexOf(':') + 1;

      const part1 = docText.substring(0, result);
      const part2 = docText.substring(result);

      doc.addStructure(
        doc.struct('P', () => {
          doc
            .font('Helvetica-Bold')
            .fontSize(12)
            .text(`${part1} `, x, y, { continued: true, width: 400, align: 'left', columns: 1 })
            .font('Helvetica')
            .fontSize(12)
            .text(`${part2} `);
        })
      );
      doc.addStructure(
        doc.struct('P', () => {
          doc
            .font('Helvetica')
            .fontSize(12)
            .text(`${value.amount} `, x + 410, y, { width: 100, align: 'right', columns: 1 });
        })
      );
      if (value.text.length > 70) {
        y += 35;
      } else {
        y += 25;
      }
    });

    doc.x = x;
    doc.y = y;
  }
}

export default function buildPdf(
  t: TFunction,
  name: ?string,
  incomeTotal: number,
  expensesTotal: number,
  incomes: OrderedMap<string, Income>,
  expenses: OrderedMap<string, Expense>
) {
  const PDFDocument = require('pdfkit/js/pdfkit.standalone');
  const blobStream = require('blob-stream-browserify');

  // Create a new PDFDocument
  const doc = new PDFDocument({
    pdfVersion: '1.7',
    lang: 'en-US',
    displayTitle: true,
    bufferPages: true,
    tagged: true,
  });

  // pipe the document to a blob
  const stream = doc.pipe(blobStream());

  const title = name ? t('pdfTitleWithName', { name: name }) : t('pdfTitle');
  const subTitle = name ? t('pdfSubTitleWithName', { name: name }) : t('pdfSubTitle');

  doc.info['Title'] = title;

  let x = 50;
  let y = 50;

  doc.addStructure(
    doc.struct('H1', () => {
      doc.font('Helvetica-Bold').fontSize(18).text(`${title} `, x, y);
    })
  );

  y += 50;
  doc.addStructure(
    doc.struct('P', () => {
      doc.font('Helvetica').fontSize(14).text(`${subTitle} `, x, y);
    })
  );

  y += 70;
  doc
    .moveTo(x, y)
    .lineWidth(2)
    .lineTo(x + 510, y)
    .stroke();

  y += 20;

  doc.addStructure(
    doc.struct('H2', () => {
      doc
        .font('Helvetica-Bold')
        .fontSize(14)
        .text(`${t('income')} `, x, y, { width: 400, align: 'left', columns: 1 });
    })
  );
  doc.addStructure(
    doc.struct('H2', () => {
      doc
        .font('Helvetica-Bold')
        .fontSize(14)
        .text(`${formatCurrency(incomeTotal)} `, x + 410, y, { width: 100, align: 'right', columns: 1 });
    })
  );
  y += 30;

  doc.x = x;
  doc.y = y;

  const incomeArray = formatIncomeRows(t, incomes) || [];
  renderDocument(doc, incomeArray);

  x = doc.x;
  y = doc.y;

  y += 20;

  doc
    .moveTo(x, y)
    .lineWidth(2)
    .lineTo(x + 510, y)
    .stroke();

  y += 20;

  doc.addStructure(
    doc.struct('H2', () => {
      doc
        .font('Helvetica-Bold')
        .fontSize(14)
        .text(`${t('expenses')} `, x, y, { width: 400, align: 'left', columns: 1 });
    })
  );
  doc.addStructure(
    doc.struct('H2', () => {
      doc
        .font('Helvetica-Bold')
        .fontSize(14)
        .text(`${formatCurrency(expensesTotal)} `, x + 410, y, { width: 100, align: 'right', columns: 1 });
    })
  );
  y += 30;

  doc.x = x;
  doc.y = y;

  const expenseArray = formatExpenseRows(t, expenses) || [];
  renderDocument(doc, expenseArray);

  x = doc.x;
  y = doc.y;

  y += 20;

  doc
    .moveTo(x, y)
    .lineWidth(2)
    .lineTo(x + 510, y)
    .stroke();

  y += 20;

  doc.addStructure(
    doc.struct('H2', () => {
      doc
        .font('Helvetica-Bold')
        .fontSize(18)
        .text(`${t('expectedYearlyIncome')} `, x, y, { width: 350, align: 'left', columns: 1 });
    })
  );
  doc.addStructure(
    doc.struct('H2', () => {
      doc
        .font('Helvetica-Bold')
        .fontSize(18)
        .text(`${formatCurrency(incomeTotal - expensesTotal)} `, x + 360, y, {
          width: 150,
          align: 'right',
          columns: 1,
        });
    })
  );

  doc.end();

  stream.on('finish', function () {
    const blob = stream.toBlob('application/pdf');

    // download the file
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = name ? `${name}-income-estimate.pdf` : 'income-estimate.pdf';
    link.click();
  });
}
